import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import FlagIcons from './FlagIcons';
import { useAuth } from '../contexts/AuthContext';
import { readInstantCache, writeInstantCache } from '../utils/instantCache';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * `PT` (Pending Task) header badge shown right of the `G` badge in the DWS
 * green header. Lists chats where the LAST message was from a customer and
 * no handler/admin has replied for ≥ 15 minutes. Oldest waits at the top.
 * Auto-refresh every 20s so overdue conversations always surface quickly.
 */
const PT_THRESHOLD_KEY = 'pt_threshold_minutes';
const PT_PRESETS = [
  { label: '0m',  value: 0 },
  { label: '15m', value: 15 },
  { label: '30m', value: 30 },
  { label: '1h',  value: 60 },
];

const InstantAccessPT = ({ variant = 'dark' }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ count: 0, tasks: [] });
  const [threshold, setThreshold] = useState(() => {
    const stored = parseInt(localStorage.getItem(PT_THRESHOLD_KEY) ?? '15', 10);
    return Number.isFinite(stored) && stored >= 0 ? stored : 15;
  });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [customVal, setCustomVal] = useState('');
  const ref = useRef(null);
  // POINT 2 — long-press (touch-and-hold) to reveal a per-entry "Clear" action.
  const pressTimerRef = useRef(null);
  const longPressedRef = useRef(false);
  const [confirmClearId, setConfirmClearId] = useState(null);
  const [clearingId, setClearingId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const persistThreshold = (mins) => {
    const n = parseInt(mins, 10);
    if (!Number.isFinite(n) || n < 0) return;
    setThreshold(n);
    localStorage.setItem(PT_THRESHOLD_KEY, String(n));
    setPickerOpen(false);
  };

  const fmtThresholdLabel = (m) => (m >= 60 ? `${m % 60 === 0 ? (m/60) : (m/60).toFixed(1)}h` : `${m}m`);

  useEffect(() => {
    let cancelled = false;
    // Fix doc #4 — instant-cache: paint last-known pending tasks, then refresh.
    const _cached = readInstantCache('pending_tasks_list', user?.id);
    if (_cached && !cancelled) setData(_cached);
    const load = async () => {
      try {
        const res = await axios.get(`${API}/chats/pending-tasks`, {
          params: { threshold_minutes: threshold, user_id: user?.id },
        });
        if (!cancelled) {
          const d = res.data || { count: 0, tasks: [] };
          setData(d);
          writeInstantCache('pending_tasks_list', user?.id, d);
        }
      } catch (_) { /* transient */ }
    };
    load();
    const t = setInterval(() => { if (document.visibilityState === 'hidden') return; load(); }, 20000);
    const onVis = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { cancelled = true; clearInterval(t); document.removeEventListener('visibilitychange', onVis); };
  }, [threshold, user?.id]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [open]);

  // P3/P4 — only one of N/G/PT/PW open at a time.
  useEffect(() => {
    const onOther = (e) => { if (e.detail !== 'PT') setOpen(false); };
    window.addEventListener('dws:ia-open', onOther);
    return () => window.removeEventListener('dws:ia-open', onOther);
  }, []);

  // Re-open the PT dropdown after back-navigation from a chat that was opened
  // via a PT list item. Uses a sessionStorage breadcrumb set inside `goto()`.
  useEffect(() => {
    try {
      if (sessionStorage.getItem('pt_reopen') === '1') {
        sessionStorage.removeItem('pt_reopen');
        setOpen(true);
      }
    } catch (_) {}
  }, []);

  const goto = (t) => {
    if (!t?.room_id) return;
    // POINT 2 — a long-press should NOT navigate into the chat; it only opens
    // the inline Clear action. Swallow the click that fires on release.
    if (longPressedRef.current) { longPressedRef.current = false; return; }
    // Mark that PT should re-open when the user returns to the dashboard.
    try { sessionStorage.setItem('pt_reopen', '1'); } catch (_) {}
    // Both handler + admin can open the same route pattern; router handles auth.
    const path = window.location.pathname.includes('/handler') ? `/handler-chat/${t.room_id}` : `/admin-chat/${t.room_id}`;
    // Preserve the origin tab so the chat's back-arrow returns the user to
    // the exact tab (Deposit/Withdrawal/Support/Inquiry) they came from.
    const originState = location.state || {};
    const chatTab = originState.activeTab || originState.chatTypeTab || (t.chat_type);
    navigate(path, { state: { activeTab: chatTab, chatTypeTab: chatTab } });
    setOpen(false);
  };

  // POINT 2 — long-press gesture: start a ~1.2s timer on press; if it fires
  // before release, reveal the "Clear" option for that entry.
  const startPress = (roomId) => {
    longPressedRef.current = false;
    clearTimeout(pressTimerRef.current);
    pressTimerRef.current = setTimeout(() => {
      longPressedRef.current = true;
      setConfirmClearId(roomId);
    }, 1200);
  };
  const endPress = () => { clearTimeout(pressTimerRef.current); };

  // POINT 2 — clear a single PT entry: resolve on backend AND remove from the
  // list immediately (optimistic), without waiting for the next poll.
  const clearEntry = async (t, e) => {
    if (e) e.stopPropagation();
    setClearingId(t.room_id);
    try {
      await axios.post(`${API}/chats/pending-tasks/resolve`, {
        room_id: t.room_id,
        actor_id: user?.id,
      });
      setData((prev) => {
        const tasks = (prev.tasks || []).filter((x) => x.room_id !== t.room_id);
        const next = { ...prev, tasks, count: tasks.length };
        writeInstantCache('pending_tasks_list', user?.id, next);
        return next;
      });
    } catch (_) {
      /* transient — next poll will reconcile */
    } finally {
      setClearingId(null);
      setConfirmClearId(null);
    }
  };

  const count = data.count || 0;
  // Time formatting for how long a chat has been waiting for a reply:
  //   < 60m       → "20m"
  //   1-23 hours  → "5h 30m"
  //   24 h+       → "10d"     (days only, no hours — clearer for very stale chats)
  const fmtWaited = (m) => {
    if (!m || m < 0) return '0m';
    if (m >= 60 * 24) return `${Math.floor(m / (60 * 24))}d`;
    if (m >= 60) return `${Math.floor(m / 60)}h ${m % 60}m`;
    return `${m}m`;
  };

  return (
    <div className="relative" ref={ref} data-testid="pending-task-pt">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => { const nv = !v; if (nv) window.dispatchEvent(new CustomEvent('dws:ia-open', { detail: 'PT' })); return nv; }); }}
        className={variant === 'light'
          ? "relative inline-flex items-center justify-center w-9 h-9 rounded-full bg-amber-500 hover:bg-amber-600 text-white font-black text-[13px] shadow-md transition-colors"
          : "relative inline-flex items-center justify-center w-9 h-9 rounded-full bg-amber-400/25 hover:bg-amber-400/40 text-white font-black text-[13px] border border-amber-200/60 shadow-inner transition-colors"}
        data-testid="pt-btn"
        title={`Pending Tasks — client messages awaiting reply (${fmtThresholdLabel(threshold)}+)`}
      >
        PT
        {count > 0 && (
          <span
            className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center border border-white shadow"
            data-testid="pt-count"
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-11 z-[80] bg-white text-slate-800 rounded-xl shadow-2xl border border-slate-200 w-[min(280px,calc(100vw-32px))] max-h-[70vh] overflow-hidden flex flex-col" data-testid="pt-menu">
          <div className="px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-slate-500 border-b border-slate-100 flex items-center justify-between">
            <span>Pending — awaiting reply</span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setPickerOpen(v => !v); }}
              className="text-amber-700 hover:text-amber-900 font-bold flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-amber-50 border border-transparent hover:border-amber-200 transition"
              title="Change threshold"
              data-testid="pt-threshold-btn"
            >
              {count} · {fmtThresholdLabel(threshold)}+ <span className="text-[9px]">▾</span>
            </button>
          </div>
          {pickerOpen && (
            <div className="px-3 py-2 border-b border-slate-100 bg-amber-50/40" data-testid="pt-threshold-picker">
              <div className="text-[10px] font-bold uppercase text-slate-500 mb-1">Show chats waiting more than</div>
              <div className="flex gap-1.5 mb-2 flex-wrap">
                {PT_PRESETS.map((p) => (
                  <button
                    key={p.value}
                    onClick={() => persistThreshold(p.value)}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition ${
                      threshold === p.value
                        ? 'bg-amber-500 text-white border-amber-500'
                        : 'bg-white text-slate-700 border-slate-200 hover:bg-amber-50'
                    }`}
                    data-testid={`pt-preset-${p.value}`}
                  >{p.label}</button>
                ))}
              </div>
              <div className="flex items-center gap-1.5">
                <input
                  type="number"
                  min="0"
                  max="1440"
                  placeholder="Custom min"
                  value={customVal}
                  onChange={(e) => setCustomVal(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') persistThreshold(customVal); }}
                  className="flex-1 px-2 py-1 border border-slate-200 rounded text-[11px] focus:outline-none focus:border-amber-500"
                  data-testid="pt-custom-input"
                />
                <button
                  onClick={() => persistThreshold(customVal)}
                  className="px-2.5 py-1 bg-amber-500 hover:bg-amber-600 text-white text-[11px] font-bold rounded"
                  data-testid="pt-custom-apply"
                >Set</button>
              </div>
            </div>
          )}
          <div className="overflow-y-auto">
            {(data.tasks || []).length === 0 ? (
              <div className="px-3 py-6 text-xs text-center text-slate-400">All caught up ✓</div>
            ) : (
              (data.tasks || []).map((t) => (
                <div
                  key={t.room_id}
                  role="button"
                  tabIndex={0}
                  onClick={() => goto(t)}
                  onTouchStart={() => startPress(t.room_id)}
                  onTouchEnd={endPress}
                  onTouchMove={endPress}
                  onMouseDown={() => startPress(t.room_id)}
                  onMouseUp={endPress}
                  onMouseLeave={endPress}
                  onContextMenu={(e) => { e.preventDefault(); longPressedRef.current = true; setConfirmClearId(t.room_id); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left border-b border-slate-100 last:border-b-0 hover:bg-amber-50 select-none cursor-pointer"
                  data-testid={`pt-item-${t.room_id}`}
                >
                  <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-700 font-bold text-sm flex-shrink-0">
                    {(t.customer_name || '?')[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="font-semibold text-[13px] truncate">{t.customer_name}</span>
                      {t.customer_flag && <FlagIcons flag={t.customer_flag} size={10} />}
                      {t.customer_mb && <span className="text-[9px] font-bold px-1 rounded bg-indigo-100 text-indigo-700 uppercase">{t.customer_mb}</span>}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate">{t.last_message || '(media)'}</div>
                  </div>
                  {confirmClearId === t.room_id ? (
                    <div className="flex items-center gap-1 flex-shrink-0 ml-1" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={(e) => clearEntry(t, e)}
                        disabled={clearingId === t.room_id}
                        className="px-2.5 py-1 rounded-full bg-red-500 hover:bg-red-600 text-white text-[11px] font-bold disabled:opacity-60"
                        data-testid={`pt-clear-btn-${t.room_id}`}
                      >
                        {clearingId === t.room_id ? '…' : 'Clear'}
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setConfirmClearId(null); }}
                        className="px-2 py-1 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 text-[11px] font-bold"
                        data-testid={`pt-clear-cancel-${t.room_id}`}
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <div className="text-[11px] font-bold text-amber-700 flex-shrink-0 whitespace-nowrap ml-1">
                      {fmtWaited(t.waited_minutes || 0)}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InstantAccessPT;
